//@flow
const vendorStateName = 'vendor'
const localStore = localStorage.getItem(vendorStateName)

export const vendorInitialState: any = localStore ? JSON.parse(localStore) : {
    vendor: {
        vendors: [],
        count: 0,
        currentVendor: null,
        loading: false,
        error: '',
        liveSyncWarnings: [],
        search: '',
        filters: {
            is_enabled: null,
            is_recur: null,
            type_of_invoice: ''
        },
        pagination: {
            page: 1,
            pageSize: 20,
            pages: 0
        },
        sortField: 'id',
        sortOrder: 'asc',
    }
}

// Reported by the API (as `liveSyncWarning`) when a vendor write succeeded in
// Postgres but the live-config write-through to DynamoDB was skipped or failed.
// The vendor is saved but not live, so this has to stay on screen until an
// operator dismisses it — see issue #36. Warnings are held one-per-vendor
// (`id` is the vendor key) rather than as a single slot, so that saving some
// unrelated vendor cannot silently wipe a warning nobody has acted on yet.
type LiveSyncWarning = {
    +id: string,
    +message: string,
    +vendor: string,
    +operation: string,
    +at: string
}

type State = {
    +vendor: {
        +vendors: Array<any>,
        +count: number,
        +currentVendor: any,
        +loading: boolean,
        +error: string,
        +liveSyncWarnings: Array<LiveSyncWarning>,
        +search: string,
        +filters: {
            +is_enabled: ?boolean,
            +is_recur: ?boolean,
            +type_of_invoice: string
        },
        +pagination: {
            +page: number,
            +pageSize: number,
            +pages: number
        },
        +sortField: string,
        +sortOrder: string
    }
}

type Action = {
    vendors?: Array<any>,
    count?: number,
    vendor?: any,
    error?: string,
    liveSyncWarning?: ?string,
    warningId?: ?string,
    search?: string,
    filters?: any,
    pagination?: any,
    pageSize?: number,
    page?: number,
    pages?: number,
    sortField?: string,
    sortOrder?: string
}

export const ActionLoadVendors = 'loadVendors'
export const ActionLoadVendorsSuccess = 'loadVendorsSuccess'
export const ActionLoadVendorsError = 'loadVendorsError'
export const ActionSelectVendor = 'selectVendor'
export const ActionCreateVendor = 'createVendor'
export const ActionCreateVendorSuccess = 'createVendorSuccess'
export const ActionUpdateVendor = 'updateVendor'
export const ActionUpdateVendorSuccess = 'updateVendorSuccess'
export const ActionDeleteVendor = 'deleteVendor'
export const ActionDeleteVendorSuccess = 'deleteVendorSuccess'
export const ActionSetSearchTerm = 'setSearchTerm'
export const ActionSetFilters = 'setFilters'
export const ActionSetPagination = 'setPagination'
export const ActionSetOrderBy = 'setOrderBy'
export const ActionClearError = 'clearError'
export const ActionClearLiveSyncWarning = 'clearLiveSyncWarning'

// Identifies the vendor a warning belongs to. The code is what ops recognises;
// the id is the fallback for a response that carries no code.
const vendorKey = (vendor: any): string => {
    const v = vendor || {}
    if (v.code) {
        return String(v.code)
    }
    return v.id !== undefined && v.id !== null ? String(v.id) : '(unknown vendor)'
}

// Folds the outcome of one write into the outstanding warnings. A write that
// reported a problem replaces any earlier warning for the same vendor; a write
// that came back clean retires the warning for *that* vendor only, on the
// grounds that the sync it complained about has now demonstrably succeeded.
// Warnings for other vendors are left alone — an operator who has not yet acted
// on one must not lose it because somebody saved an unrelated vendor.
const mergeLiveSyncWarning = (state: State, action: Action, operation: string): Array<LiveSyncWarning> => {
    const existing = state.vendor.liveSyncWarnings || []
    const key = vendorKey(action.vendor)
    const others = existing.filter(w => w.id !== key)
    if (!action.liveSyncWarning) {
        return others
    }
    return [...others, {
        id: key,
        message: action.liveSyncWarning,
        vendor: key === '(unknown vendor)' ? '' : key,
        operation,
        at: new Date().toISOString()
    }]
}

export const vendorActions: any = {
    [ActionLoadVendors]: (state: State) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                loading: true, 
                error: '' 
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionLoadVendorsSuccess]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                vendors: action.vendors || [],
                count: action.count || 0,
                pagination: { 
                    ...state.vendor.pagination,
                    page: action.page || 1, 
                    pageSize: action.pageSize || 20,
                    pages: action.pages || 0
                },
                sortField: action.sortField || state.vendor.sortField,
                sortOrder: action.sortOrder || state.vendor.sortOrder,
                loading: false, 
                error: '' 
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionLoadVendorsError]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                loading: false, 
                error: action.error || 'Failed to load vendors' 
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionSelectVendor]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                currentVendor: action.vendor 
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionCreateVendorSuccess]: (state: State, action: Action) => {
        const vendors = [...state.vendor.vendors, action.vendor];
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                vendors,
                count: state.vendor.count + 1,
                error: '',
                liveSyncWarnings: mergeLiveSyncWarning(state, action, 'create')
            }
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionUpdateVendorSuccess]: (state: State, action: Action) => {
        const vendors = state.vendor.vendors.map(v => 
            v.id === action.vendor?.id ? action.vendor : v
        );
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                vendors,
                currentVendor: action.vendor,
                error: '',
                liveSyncWarnings: mergeLiveSyncWarning(state, action, 'update')
            }
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionDeleteVendorSuccess]: (state: State, action: Action) => {
        const vendors = state.vendor.vendors.filter(v => v.id !== action.vendor?.id);
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                vendors,
                count: state.vendor.count - 1,
                currentVendor: state.vendor.currentVendor?.id === action.vendor?.id ? null : state.vendor.currentVendor,
                error: '',
                liveSyncWarnings: mergeLiveSyncWarning(state, action, 'delete')
            }
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionSetSearchTerm]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                search: action.search || '',
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionSetFilters]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                filters: { ...state.vendor.filters, ...action.filters },
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionSetPagination]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                pagination: { ...state.vendor.pagination, ...action.pagination }
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionSetOrderBy]: (state: State, action: Action) => {
        const vendorState = { 
            vendor: { 
                ...state.vendor, 
                sortField: action.sortField || 'id',
                sortOrder: action.sortOrder || 'asc'
            } 
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    [ActionClearError]: (state: State) => {
        const vendorState = {
            vendor: {
                ...state.vendor,
                error: ''
            }
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    },
    // Dismissal is per warning: acknowledging one vendor says nothing about the
    // others. Omitting the id clears the lot, which is only used deliberately.
    [ActionClearLiveSyncWarning]: (state: State, action: Action) => {
        const existing = state.vendor.liveSyncWarnings || []
        const vendorState = {
            vendor: {
                ...state.vendor,
                liveSyncWarnings: action.warningId
                    ? existing.filter(w => w.id !== action.warningId)
                    : []
            }
        };
        localStorage.setItem(vendorStateName, JSON.stringify(vendorState))
        return vendorState
    }
};

export const getStateName = (): string => vendorStateName;
